/* eslint react/destructuring-assignment: 0 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { HotKeys } from 'react-hotkeys';
import { matchPath, withRouter } from 'react-router-dom';
import { isDirty, getFormValues } from 'redux-form';
import ih from 'immutability-helper';
import { isEmpty } from 'lodash';
import Preloader from '@omilia/preloader';

import COMBOS from 'constants/keyboardShortcutsCombos';
import {
  entityType,
  sortOptionType,
  filterOptionsType,
  paginationOptionsType,
  featureTypesType,
  annotationType,
} from 'types';

import withPartialImportModal from 'components/withPartialImportModal';
import { withExportingModal } from 'components/PartialExportModal';
import Splitter from 'components/Splitter';
import UnitsSearchResult from 'components/Units/UnitsSearchResult';
import NoUnits from 'components/Units/NoUnits';
import Pagination from 'components/Pagination';
import UnsavedChangesModal from 'components/UnsavedChangesModal';
import SortingMenu from 'components/SortingMenu';
import FilterMenu from 'components/FilterMenu';
import ListHeaderOptions from 'components/ListHeaderOptions';
import AnnotationDrawer from 'components/MatchedAnnotationDrawer';

import FORM_NAMES from 'constants/formNames';

import { constructFeaturesToSave } from 'store/utils/entities';
import { getHasSelectedTestSet } from 'store/services/testSets/selectors';
import * as testSetsActions from 'store/services/testSets/actions';
import * as actions from 'store/services/entities/actions';
import * as selectors from 'store/services/entities/selectors';
import * as entitiesFeaturesActions from 'store/services/entitiesFeatures/actions';
import * as entitiesFeaturesSelectors from 'store/services/entitiesFeatures/selectors';
import * as annotationsActions from 'store/services/annotations/actions';
import * as annotationsSelectors from 'store/services/annotations/selectors';

import TestSets from 'scenes/TestSets';

import { ENTITY_SCOPE_TYPES } from './types';

import List from './components/EntitiesList';
import Inspect from './components/EntityInspect';
import CreateNewEntityModal from './components/CreateNewEntityModal';

import './style.scss';

const HOTKEYS_MAP = {
  create: COMBOS.createNew,
};

export const formatInspectingItemUpdatesPreSubmit = (inspectingUpdates = {}) =>
  ih(inspectingUpdates, {
    scope: {
      $set: inspectingUpdates.public
        ? ENTITY_SCOPE_TYPES.PUBLIC
        : ENTITY_SCOPE_TYPES.PRIVATE,
    },
    $unset: ['public', 'features'],
  });

const isShowingImportModal = (location, resource) =>
  !!matchPath(location.pathname, {
    exact: true,
    path: `/:company/:project/${resource}/import/:state`,
  });

export class Entities extends Component {
  handleHotKeys = {
    create: this.handleOpenCreateModal,
  };

  static propTypes = {
    addEntity: PropTypes.func.isRequired,
    annotationsIsLoaded: PropTypes.bool.isRequired,
    count: PropTypes.number.isRequired,
    currentEntityIndex: PropTypes.number.isRequired,
    getEntities: PropTypes.func.isRequired,
    hasSelectedTestSet: PropTypes.bool.isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    inspectingDirty: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        company: PropTypes.string.isRequired,
        project: PropTypes.string.isRequired,
      }).isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired,
    partialExport: PropTypes.func.isRequired,
    selectEntity: PropTypes.func.isRequired,
    onCheckEntityName: PropTypes.func.isRequired,
    onGetTestSets: PropTypes.func.isRequired,
    onItemDeselect: PropTypes.func.isRequired,
    onItemFeaturesUpdate: PropTypes.func.isRequired,
    onItemUpdate: PropTypes.func.isRequired,
    annotationsMatched: PropTypes.arrayOf(annotationType),
    entities: PropTypes.arrayOf(entityType),
    filterOptions: PropTypes.arrayOf(filterOptionsType),
    inspectingItem: entityType,
    inspectingItemFeatures: PropTypes.arrayOf(featureTypesType),
    inspectingUpdates: entityType,
    paginationOptions: paginationOptionsType,
    selectedSortOption: sortOptionType,
    sortOptions: PropTypes.arrayOf(sortOptionType),
    onAnnotatorToggleAllVisibility: PropTypes.func,
    onAnnotatorToggleVisibility: PropTypes.func,
  };

  state = {
    isCreateModalOpen: false,
    selectedTabIndex: 1,
    unsavedChangesModal: {
      isOpen: false,
      rejectClb: null,
      confirmClb: null,
    },
  };

  componentDidMount = () => {
    this.props.getEntities({
      page: 1,
      size: 50,
      name__icontains: '',
    });
  };

  componentWillReceiveProps({ inspectingItem }) {
    // TODO: handle this weird logic
    if (
      !isShowingImportModal(this.props.location, 'entities') &&
      !isEmpty(this.props.inspectingItem) &&
      isEmpty(inspectingItem)
    ) {
      this.props.history.push(this.props.match.url);
    }
  }

  componentDidUpdate({ match }) {
    if (
      match.params.company !== this.props.match.params.company ||
      match.params.project !== this.props.match.params.project
    ) {
      // FIXME: Remove `onGetTestSets` when test-sets ported ro react
      this.props.onGetTestSets();
      this.props.getEntities({
        page: 1,
        size: 50,
        name__icontains: '',
      });
    }
  }

  getFilteredEntities = params => {
    this.props.getEntities({ page: 1 }, params);
  };

  handleOpenCreateModal = () => {
    this.setState({
      isCreateModalOpen: true,
    });
  };

  handleCloseCreateModal = () => {
    this.setState({
      isCreateModalOpen: false,
    });
  };

  handleAddEntity = async ({ entityName }) => {
    const id = await this.props.addEntity({
      name: entityName,
    });

    if (id) {
      this.props.history.push(`${this.props.match.url}/${id}`);

      this.handleCloseCreateModal();
      this.handleChangeTab(1);
    }
  };

  handleChangeTab = index => {
    this.setState({
      selectedTabIndex: index,
    });
  };

  handleInspectClose = () => {
    if (this.props.inspectingDirty) {
      this.handleOpenUnsavedChangesModal(this.handleItemUpdate, () => {
        this.handleCloseUnsavedChangesModal();
        this.props.onItemDeselect();
      });
    } else {
      this.props.onItemDeselect();
    }
  };

  handleItemUpdate = async () => {
    const {
      inspectingItem,
      inspectingUpdates,
      onItemUpdate,
      onItemFeaturesUpdate,
    } = this.props;

    const inspectingItemsUpdatesFormatted = formatInspectingItemUpdatesPreSubmit(
      inspectingUpdates,
    );

    await onItemUpdate(inspectingItem.id, inspectingItemsUpdatesFormatted);

    if (!inspectingItem.xpack_resource) {
      await onItemFeaturesUpdate(
        inspectingItem.id,
        constructFeaturesToSave(inspectingUpdates.features),
      );
    }
  };

  handleListItemSelect = item => {
    const {
      history,
      match,
      inspectingItem,
      inspectingDirty,
      selectEntity,
    } = this.props;

    const pendingAction = () => {
      history.push(`${match.url}/${item.id}`);
      selectEntity(item);
    };

    if (isEmpty(inspectingItem)) {
      pendingAction();
      return;
    }

    if (inspectingDirty) {
      this.handleOpenUnsavedChangesModal(
        async () => {
          await this.handleItemUpdate();

          pendingAction();
        },
        () => {
          // FIXME: Seems UnsavedChangesModal should close itself for `Do Not Save` action
          // and should behave same as `Save`
          this.handleCloseUnsavedChangesModal();

          pendingAction();
        },
      );

      return;
    }

    pendingAction();
  };

  handleSearchReset = () => {
    this.props.getEntities({
      page: 1,
      name__icontains: '',
    });
  };

  handleOpenUnsavedChangesModal = (confirmClb, rejectClb) => {
    this.setState({
      unsavedChangesModal: {
        isOpen: true,
        rejectClb,
        confirmClb,
      },
    });
  };

  handleCloseUnsavedChangesModal = () => {
    this.setState({
      unsavedChangesModal: {
        isOpen: false,
        rejectClb: null,
        confirmClb: null,
      },
    });
  };

  handleMergeImport = () => {
    this.props.history.push(`${this.props.match.url}/import/merge`);
  };

  handleReplaceImport = () => {
    this.props.history.push(`${this.props.match.url}/import/replace`);
  };

  renderInspect = () => {
    const { selectedTabIndex } = this.state;
    const {
      location,
      entities,
      inspectingItem,
      inspectingItemFeatures,
      onItemDeselect,
    } = this.props;

    const match = matchPath(location.pathname, {
      exact: true,
      path: '/:company/:project/entities/:entity',
    });

    if (match && entities.length > 0) {
      return (
        <Inspect
          changeTab={this.handleChangeTab}
          className="entity-inspect-wrapper"
          currentEntity={{
            ...inspectingItem,
            features: inspectingItemFeatures,
          }}
          id={match.params.entity}
          selectedTabIndex={selectedTabIndex}
          onClose={this.handleInspectClose}
          onItemDeselect={onItemDeselect}
          onSubmit={this.handleItemUpdate}
        />
      );
    }

    return null;
  };

  renderModals = () => {
    const { isCreateModalOpen, unsavedChangesModal } = this.state;

    return (
      <React.Fragment>
        {isCreateModalOpen && (
          <CreateNewEntityModal
            onCheckEntityName={this.props.onCheckEntityName}
            onClose={this.handleCloseCreateModal}
            onSubmit={this.handleAddEntity}
          />
        )}
        {unsavedChangesModal.isOpen && (
          <UnsavedChangesModal
            isOpen
            onCancel={this.handleCloseUnsavedChangesModal}
            onDiscard={unsavedChangesModal.rejectClb}
          />
        )}
      </React.Fragment>
    );
  };

  renderList = () => {
    const {
      isLoading,
      entities,
      inspectingItem,
      currentEntityIndex,
      paginationOptions,
    } = this.props;

    if (isLoading) {
      return <Preloader />;
    }

    if (entities.length > 0) {
      return (
        <List
          currentEntityIndex={currentEntityIndex}
          deselectEntity={this.handleInspectClose}
          entities={entities}
          selectedEntity={inspectingItem}
          selectEntity={this.handleListItemSelect}
        />
      );
    }

    if (paginationOptions.name__icontains !== '') {
      return (
        <UnitsSearchResult
          items={entities}
          searchTerm={paginationOptions.name__icontains}
          showAll={this.handleSearchReset}
          unitName="entity"
        />
      );
    }

    return (
      <NoUnits openModal={this.handleOpenCreateModal} unitName="entities" />
    );
  };

  render() {
    const {
      count,
      paginationOptions,
      partialExport,
      getEntities,
      entities,
      sortOptions,
      selectedSortOption,
      filterOptions,
      hasSelectedTestSet,
      annotationsIsLoaded,
      annotationsMatched,
      onAnnotatorToggleVisibility,
      onAnnotatorToggleAllVisibility,
    } = this.props;

    return (
      <Splitter split="vertical">
        <TestSets />

        <HotKeys
          handlers={this.handleHotKeys}
          keyMap={HOTKEYS_MAP}
          styleName="root"
        >
          {this.renderModals()}

          <ListHeaderOptions
            bottomRightOptions={
              <React.Fragment>
                <FilterMenu
                  filterOptions={filterOptions}
                  modelName="entity"
                  onFilter={this.getFilteredEntities}
                />

                <SortingMenu
                  getItems={getEntities}
                  iconClassName="icon-sorting-az"
                  items={entities}
                  options={sortOptions}
                  selectedSortOption={selectedSortOption}
                />

                {hasSelectedTestSet && annotationsIsLoaded && (
                  <AnnotationDrawer
                    matchedAnnotations={annotationsMatched}
                    toggleAll={onAnnotatorToggleAllVisibility}
                    toggleAnnotation={onAnnotatorToggleVisibility}
                  />
                )}
              </React.Fragment>
            }
            getItems={getEntities}
            items={entities}
            modelName="entity"
            openNewModal={this.handleOpenCreateModal}
            searchTerm={paginationOptions.name__icontains}
            onExport={partialExport}
            onMergeImport={this.handleMergeImport}
            onReplaceImport={this.handleReplaceImport}
          />

          <div className="tabs-content right-tabs height-184">
            <Splitter split="horizontal">
              {this.renderList()}
              {this.renderInspect()}
            </Splitter>
          </div>

          {entities.length > 0 && (
            <Pagination
              className="footer__pagination"
              count={count}
              getItems={getEntities}
              paginationOptions={paginationOptions}
            />
          )}
        </HotKeys>
      </Splitter>
    );
  }
}

const mapStateToProps = state => ({
  isExporting: selectors.getIsExporting(state),
  isLoading: selectors.getIsLoadingList(state),
  entities: selectors.getEntitiesList(state),
  count: selectors.getEntitiesListCount(state),
  paginationOptions: selectors.getPaginationOptions(state),
  filterOptions: selectors.getFilterOptions(state),
  sortOptions: selectors.getFilteredSortOptions(state),
  selectedSortOption: selectors.getSelectedSortOption(state),
  currentEntityIndex: selectors.getEntityIndex(state),
  hasSelectedTestSet: getHasSelectedTestSet(state),

  // INSPECT
  inspectingItem: selectors.getEntity(state),
  inspectingItemFeatures: entitiesFeaturesSelectors.getEntitiesFeaturesList(
    state,
  ),
  inspectingDirty: isDirty(FORM_NAMES.ENTITIES.INSPECT)(state),
  inspectingUpdates: getFormValues(FORM_NAMES.ENTITIES.INSPECT)(state),

  // ANNOTATIONS:
  annotationsIsLoaded: annotationsSelectors.getIsLoaded(state),
  annotationsMatched: annotationsSelectors.getAllMatchedAnnotations(state),
});

const mapDispatchToProps = {
  onGetTestSets: testSetsActions.getTestSetsList,
  cancelExport: actions.cancelExportEntities,
  onItemUpdate: actions.updateEntity,
  onItemDeselect: actions.deselectEntity,

  onItemFeaturesUpdate: entitiesFeaturesActions.multiEntitiesFeature,
  partialExport: actions.partialExportEntities,
  getEntities: actions.getEntitiesList,
  addEntity: actions.addEntity,
  selectEntity: actions.selectEntity,
  onCheckEntityName: actions.checkEntityName,

  // ANNOTATIONS:
  onAnnotatorToggleVisibility: annotationsActions.toggleAnnotationVisibility,
  onAnnotatorToggleAllVisibility:
    annotationsActions.toggleAnnotationsVisibility,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withPartialImportModal('entities'),
  withExportingModal('Entities'),
  withRouter,
)(Entities);
